import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router"
import { Suspense } from "react"
import { Toaster } from "sonner"
import { StoreCleanupProvider } from "@/components/StoreCleanupProvider"
import { DashboardLayout } from "@/layouts/DashboardLayout"
import { lazyWithRetry } from "@/lib/lazy-with-retry"
import { OptimGovPageLoader } from "@/components/ui/optimgov-loader"

// Lazy-load all pages for code splitting
const SuperAdminApp = lazyWithRetry(() => import("@/pages/superadmin/SuperAdminApp"))
const SetPasswordPage = lazyWithRetry(() => import("@/pages/SetPasswordPage"))
const ForgotPasswordPage = lazyWithRetry(() => import("@/pages/ForgotPasswordPage"))
const HomePage = lazyWithRetry(() => import("@/pages/HomePage"))
const TendersPage = lazyWithRetry(() => import("@/pages/TendersPage"))
const TendersImportPage = lazyWithRetry(() => import("@/pages/TendersImportPage"))
const ExpedientDetailPage = lazyWithRetry(() => import("@/pages/ExpedientDetailPage"))
const DocumentEditorPage = lazyWithRetry(() => import("@/pages/DocumentEditorPage"))
const ExpedientEditorPage = lazyWithRetry(() => import("@/pages/ExpedientEditorPage"))
const ReportDetailPage = lazyWithRetry(() => import("@/pages/ReportDetailPage"))
const TenderAssistantSessionPage = lazyWithRetry(() => import("@/pages/TenderAssistantSessionPage"))
const ProcurementTemplatesPage = lazyWithRetry(() => import("@/pages/ProcurementTemplatesPage"))
const TemplateImportPage = lazyWithRetry(() => import("@/pages/TemplateImportPage"))
const ProcurementTemplateDetailPage = lazyWithRetry(() => import("@/pages/ProcurementTemplateDetailPage"))
const PublicTendersPage = lazyWithRetry(() => import("@/pages/PublicTendersPage"))
const PublicTenderDetailPage = lazyWithRetry(() => import("@/pages/PublicTenderDetailPage"))
const OrganizationsPage = lazyWithRetry(() => import("@/pages/OrganizationsPage"))
const OrganizationDetailPage = lazyWithRetry(() => import("@/pages/OrganizationDetailPage"))
const NewEvaluationPage = lazyWithRetry(() => import("@/pages/NewEvaluationPage"))
const EvaluationFromDocsPage = lazyWithRetry(() => import("@/pages/EvaluationFromDocsPage"))
const EvaluatorPage = lazyWithRetry(() => import("@/pages/EvaluatorPage"))
const EvaluatorHistoryPage = lazyWithRetry(() => import("@/pages/EvaluatorHistoryPage"))
const EvaluatorHistoryDetailPage = lazyWithRetry(() => import("@/pages/EvaluatorHistoryDetailPage"))
const BundleDetailPage = lazyWithRetry(() => import("@/pages/BundleDetailPage"))
const TemplatesPage = lazyWithRetry(() => import("@/pages/TemplatesPage"))
const TemplateDetailPage = lazyWithRetry(() => import("@/pages/TemplateDetailPage"))
const TemplateEditPage = lazyWithRetry(() => import("@/pages/TemplateEditPage"))
const NewTemplatePage = lazyWithRetry(() => import("@/pages/NewTemplatePage"))
const TemplateGroupPage = lazyWithRetry(() => import("@/pages/TemplateGroupPage"))
const AdminPage = lazyWithRetry(() => import("@/pages/AdminPage"))
const BrandPage = lazyWithRetry(() => import("@/pages/BrandPage"))
const SettingsPage = lazyWithRetry(() => import("@/pages/SettingsPage"))
const RoleLabelsSettingsPage = lazyWithRetry(() => import("@/pages/RoleLabelsSettingsPage"))
const OrganizationPage = lazyWithRetry(() => import("@/pages/OrganizationPage"))
const AuditPage = lazyWithRetry(() => import("@/pages/AuditPage"))
const SearchPage = lazyWithRetry(() => import("@/pages/SearchPage"))
const TemplateDocumentPage = lazyWithRetry(() => import("@/pages/TemplateDocumentPage"))
const TemplateEditorPage = lazyWithRetry(() => import("@/pages/TemplateEditorPage"))
const SectionLibraryPage = lazyWithRetry(() => import("@/pages/SectionLibraryPage"))
const IjToolPage = lazyWithRetry(() => import("@/pages/IjToolPage"))
const NewPage = lazyWithRetry(() => import("@/pages/NewPage"))
const LoginPage = lazyWithRetry(() => import("@/pages/LoginPage"))
const CreateAdminPage = lazyWithRetry(() => import("@/pages/CreateAdminPage"))
const VerifyEmailPage = lazyWithRetry(() => import("@/pages/VerifyEmailPage"))

export function App() {
	return (
		<StoreCleanupProvider>
			<BrowserRouter basename={(window as any).__CONTRACTACIO_CONFIG__?.basePath ?? ""}>
				<Suspense fallback={<OptimGovPageLoader />}>
					<Routes>
						{/* Auth pages (no dashboard layout) */}
						<Route path='/login' element={<LoginPage />} />
						<Route path='/set-password' element={<SetPasswordPage />} />
						<Route path='/forgot-password' element={<ForgotPasswordPage />} />
						<Route path='/create-admin' element={<CreateAdminPage />} />
						<Route path='/verify-email' element={<VerifyEmailPage />} />

						{/* Decoupled superadmin backoffice — own URL, own login, no dashboard layout */}
						<Route path='/superadmin/*' element={<SuperAdminApp />} />

					{/* Dashboard pages (with sidebar + header) */}
						<Route
							element={
								<DashboardLayout>
									<Outlet />
								</DashboardLayout>
							}
						>
							<Route index element={<HomePage />} />
							<Route path='tenders' element={<TendersPage />} />
							<Route path='tenders/import' element={<TendersImportPage />} />
							<Route path='expedients/:id' element={<ExpedientDetailPage />} />
							<Route path='expedients/:id/editor' element={<ExpedientEditorPage />} />
							<Route path='expedients/:id/documents/:reportId' element={<DocumentEditorPage />} />
							<Route path='reports/:id' element={<ReportDetailPage />} />
							{/* The Assistent history page is gone — drafts live in the expedients list */}
						<Route path='tender-assistant' element={<Navigate to='/tenders' replace />} />
							<Route path='tender-assistant/:sessionId' element={<TenderAssistantSessionPage />} />
							<Route path='procurement-templates' element={<ProcurementTemplatesPage />} />
							<Route path='procurement-templates/import' element={<TemplateImportPage />} />
							<Route path='procurement-templates/:id' element={<ProcurementTemplateDetailPage />} />
							<Route path='procurement-templates/:id/editor' element={<TemplateEditorPage />} />
							<Route path='procurement-templates/:id/documents/:reportId' element={<TemplateDocumentPage />} />
							<Route path='public-tenders' element={<PublicTendersPage />} />
							<Route path='public-tenders/:id' element={<PublicTenderDetailPage />} />
							<Route path='public-tenders/organizations' element={<OrganizationsPage />} />
							<Route path='public-tenders/organizations/:id' element={<OrganizationDetailPage />} />
							<Route path='evaluations/new' element={<NewEvaluationPage />} />
							<Route path='evaluations/new/from-documents' element={<EvaluationFromDocsPage />} />
							<Route path='evaluator' element={<EvaluatorPage />} />
							<Route path='evaluator/history' element={<EvaluatorHistoryPage />} />
							<Route path='evaluator/history/:id' element={<EvaluatorHistoryDetailPage />} />
							<Route path='bundles/:id' element={<BundleDetailPage />} />
							<Route path='templates' element={<TemplatesPage />} />
							<Route path='templates/new' element={<NewTemplatePage />} />
							<Route path='templates/:id' element={<TemplateDetailPage />} />
							<Route path='templates/:id/edit' element={<TemplateEditPage />} />
							<Route path='templates/group/:groupId' element={<TemplateGroupPage />} />
							<Route path='admin' element={<AdminPage />} />
							<Route path='brand' element={<BrandPage />} />
							<Route path='settings' element={<SettingsPage />} />
							<Route path='settings/role-labels' element={<RoleLabelsSettingsPage />} />
							<Route path='organization' element={<OrganizationPage />} />
							<Route path='organization/users' element={<OrganizationPage />} />
							<Route path='organization/teams' element={<OrganizationPage />} />
							{/* Legacy redirects */}
							<Route path='users' element={<OrganizationPage />} />
							<Route path='teams' element={<OrganizationPage />} />
							<Route path='admin/audit' element={<AuditPage />} />
							<Route path='search' element={<SearchPage />} />
							<Route path='section-library' element={<SectionLibraryPage />} />
							<Route path='ij-tool' element={<IjToolPage />} />
							<Route path='new' element={<NewPage />} />
						</Route>
					</Routes>
				</Suspense>
			</BrowserRouter>
			<Toaster position='bottom-right' richColors />
		</StoreCleanupProvider>
	)
}
